import BottomSheetImpl from './bottom-sheet.js';
import MojitoCore from 'mojito/core';
import PropTypes from 'prop-types';

const UIView = MojitoCore.Presentation.UIView;

/**
 * Bottom sheet state enum.
 *
 * @readonly
 * @name BottomSheetState
 * @enum {string}
 * @memberof Mojito.Presentation.Components
 */
export const STATE = {
    CLOSED: 'closed',
    PARTLY_OPENED: 'partly-opened',
    OPENED: 'opened',
};

/**
 * Bottom sheet component.
 * A bottom sheet is a user interface component that slides up from the bottom of the screen to present supplementary content.
 * It's often used in mobile to show more information related to the main view without navigating away from it.
 * The user can usually swipe down on the bottom sheet to dismiss it.
 * The swipe up action will call the callback.
 *
 * @class BottomSheet
 * @memberof Mojito.Presentation.Components
 */

/**
 * BottomSheet prop types.
 *
 * @property {Mojito.Presentation.Components.BottomSheetState} [state] - Determines whether the bottom sheet is opened, closed or partly opened.
 * @property {Function} [onSwipeUp] - Callback function triggered when the user swipes up on the bottom sheet.
 * @property {Function} [onSwipeDown] - Callback function triggered when the user swipes down on the bottom sheet.
 * @property {React.ReactNode|React.ReactNode[]} [headerElement] - The content to display in the header of the bottom sheet.
 * @property {React.ReactNode|React.ReactNode[]} [children] - The content to display inside the bottom sheet.
 *
 *
 * @memberof Mojito.Presentation.Components.BottomSheet
 */
const propTypes = {
    state: PropTypes.string,
    onSwipeUp: PropTypes.func,
    onSwipeDown: PropTypes.func,
    headerElement: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
};

const defaultProp = {
    state: STATE.CLOSED,
};

export default UIView('BottomSheet', BottomSheetImpl, propTypes, defaultProp);
